import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Alert,
  Divider,
  Switch,
  FormControlLabel,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Paper,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Security as SecurityIcon,

  VerifiedUser as VerifiedIcon,
  Cancel as NotVerifiedIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { updateProfile, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { db, analytics } from '../config/firebase';
import { logEvent } from 'firebase/analytics';

import pointsService from '../services/pointsService';

const MotionCard = motion(Card);

// Country list
const countries = [
  'Afghanistan', 'Albania', 'Algeria', 'Argentina', 'Armenia', 'Australia',
  'Austria', 'Azerbaijan', 'Bahrain', 'Bangladesh', 'Belarus', 'Belgium',
  'Bolivia', 'Bosnia and Herzegovina', 'Brazil', 'Bulgaria', 'Cambodia',
  'Canada', 'Chile', 'China', 'Colombia', 'Croatia', 'Czech Republic',
  'Denmark', 'Ecuador', 'Egypt', 'Estonia', 'Ethiopia', 'Finland',
  'France', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Hungary',
  'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
  'Israel', 'Italy', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya',
  'Kuwait', 'Latvia', 'Lebanon', 'Lithuania', 'Luxembourg', 'Malaysia',
  'Mexico', 'Morocco', 'Netherlands', 'New Zealand', 'Nigeria', 'North Macedonia',
  'Norway', 'Pakistan', 'Peru', 'Philippines', 'Poland', 'Portugal',
  'Qatar', 'Romania', 'Russia', 'Saudi Arabia', 'Serbia', 'Singapore',
  'Slovakia', 'Slovenia', 'South Africa', 'South Korea', 'Spain', 'Sri Lanka',
  'Sweden', 'Switzerland', 'Thailand', 'Turkey', 'Ukraine', 'United Arab Emirates',
  'United Kingdom', 'United States', 'Uruguay', 'Venezuela', 'Vietnam'
];

// Timezone list
const timezones = [
  'UTC-12:00 (Baker Island)',
  'UTC-11:00 (American Samoa)',
  'UTC-10:00 (Hawaii)',
  'UTC-09:00 (Alaska)',
  'UTC-08:00 (Pacific Time)',
  'UTC-07:00 (Mountain Time)',
  'UTC-06:00 (Central Time)',
  'UTC-05:00 (Eastern Time)',
  'UTC-04:00 (Atlantic Time)',
  'UTC-03:00 (Argentina, Brazil)',
  'UTC-02:00 (Mid-Atlantic)',
  'UTC-01:00 (Azores)',
  'UTC+00:00 (GMT/UTC)',
  'UTC+01:00 (Central European)',
  'UTC+02:00 (Eastern European)',
  'UTC+03:00 (Moscow, Turkey)',
  'UTC+04:00 (Gulf Standard)',
  'UTC+05:00 (Pakistan)',
  'UTC+05:30 (India)',
  'UTC+06:00 (Bangladesh)',
  'UTC+07:00 (Thailand, Vietnam)',
  'UTC+08:00 (China, Singapore)',
  'UTC+09:00 (Japan, Korea)',
  'UTC+10:00 (Australia East)',
  'UTC+11:00 (Solomon Islands)',
  'UTC+12:00 (New Zealand)'
];

interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  mobileNumber: string;
  isAdmin: boolean;
  createdAt: any;
  lastLogin: any;

  fcmTokens?: string[];
  // Additional profile fields
  firstName?: string;
  lastName?: string;
  bio?: string;
  country?: string;
  tradingExperience?: string;
  preferredTradingPairs?: string;
  timezone?: string;
  newsletter?: boolean;
  // Trading Account Information
  xmAccountNumber?: string;
  weltradeAccountNumber?: string;
  weltradeUserId?: string;
  derivAccountNumber?: string;
  // Affiliation Status
  xmAffiliationVerified?: boolean;
  xmAffiliationVerifiedAt?: any;
}

export const Profile: React.FC = () => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Detect device type for analytics
  const deviceType = isMobile ? 'mobile' : 'desktop';

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [affiliationStatus, setAffiliationStatus] = useState<'unchecked' | 'checking' | 'affiliated' | 'not-affiliated' | 'error'>('unchecked');
  const [affiliationMessage, setAffiliationMessage] = useState('');

  // Form data for editing
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    mobileNumber: '',
    bio: '',
    country: '',
    tradingExperience: '',
    preferredTradingPairs: '',
    timezone: '',
    newsletter: false,

    // Trading Account Information
    xmAccountNumber: '',
    weltradeAccountNumber: '',
    weltradeUserId: '',
    derivAccountNumber: '',
    // Affiliation Status (not editable in form, but loaded from profile)
    xmAffiliationVerified: false,
  });

  // Load user profile data
  useEffect(() => {
    const loadProfile = async () => {
      if (!currentUser) return;

      try {
        // Track page view
        if (analytics) {
          logEvent(analytics, 'page_view', {
            page_title: 'Profile',
            page_location: window.location.href,
            device_type: deviceType,
            user_id: currentUser.uid
          });
        }

        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data() as UserProfile;
          setProfile(data);
          
          // Parse display name into first and last name
          const [firstName = '', lastName = ''] = (data.displayName || '').split(' ');
          
          setFormData({
            firstName,
            lastName,
            mobileNumber: data.mobileNumber || '',
            bio: data.bio || '',
            country: data.country || '',
            tradingExperience: data.tradingExperience || '',
            preferredTradingPairs: data.preferredTradingPairs || '',
            timezone: data.timezone || '',
            newsletter: data.newsletter || false,

            // Trading Account Information
            xmAccountNumber: data.xmAccountNumber || '',
            weltradeAccountNumber: data.weltradeAccountNumber || '',
            weltradeUserId: data.weltradeUserId || '',
            derivAccountNumber: data.derivAccountNumber || '',
            // Affiliation Status
            xmAffiliationVerified: data.xmAffiliationVerified || false,
          });
        }
      } catch (error) {
        setError('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [currentUser]);

  // Set initial affiliation status based on loaded profile
  useEffect(() => {
    if (profile?.xmAffiliationVerified && formData.xmAccountNumber) {
      setAffiliationStatus('affiliated');
      setAffiliationMessage('✅ Account is affiliated with T1 FX Team');
    }
  }, [profile?.xmAffiliationVerified, formData.xmAccountNumber]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement> | any) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Reset affiliation status when XM account number changes
    if (name === 'xmAccountNumber') {
      setAffiliationStatus('unchecked');
      setAffiliationMessage('');
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSaveProfile = async () => {
    if (!currentUser) return;

    setSaving(true);
    setError('');
    setSuccess('');

    // Track profile save attempt
    if (analytics) {
      logEvent(analytics, 'profile_save_attempt', {
        device_type: deviceType,
        user_id: currentUser.uid,
        has_xm_account: !!(formData.xmAccountNumber?.trim()),
        affiliation_status: affiliationStatus
      });
    }

    try {
      // Require XM account number when editing profile
      if (!formData.xmAccountNumber || formData.xmAccountNumber.trim() === '') {
        setError('XM Account Number is required. Please enter your XM trading account number.');
        
        // Track validation error
        if (analytics) {
          logEvent(analytics, 'profile_save_error', {
            error_type: 'missing_xm_account',
            error_message: 'XM Account Number is required',
            device_type: deviceType,
            user_id: currentUser.uid
          });
        }
        
        setSaving(false);
        return;
      }

      // Check if XM account number has changed and determine if we need to reset verification
      const currentXmAccount = profile?.xmAccountNumber || '';
      const newXmAccount = formData.xmAccountNumber?.trim() || '';
      const xmAccountChanged = newXmAccount !== currentXmAccount.trim();
      
      // Validate XM account verification status
      if (newXmAccount && newXmAccount !== '') {
        // If user has entered an XM account number, it must be verified
        if (xmAccountChanged || affiliationStatus !== 'affiliated') {
          setError('Please verify your XM account before saving. Click the "Check" button next to your XM account number.');
          
          // Track verification error
          if (analytics) {
            logEvent(analytics, 'profile_save_error', {
              error_type: 'xm_account_not_verified',
              error_message: 'XM account not verified',
              device_type: deviceType,
              user_id: currentUser.uid,
              xm_account_changed: xmAccountChanged,
              affiliation_status: affiliationStatus
            });
          }
          
          setSaving(false);
          return;
        }
      }
      
      // Check for duplicate XM account number if it's provided and different from current
      if (formData.xmAccountNumber && formData.xmAccountNumber.trim() !== '') {
        // Only check for duplicates if the XM account number has changed
        if (xmAccountChanged) {
          const duplicateCheck = await pointsService.checkDuplicateXmAccount(
            formData.xmAccountNumber.trim(), 
            currentUser.uid
          );
          
          if (duplicateCheck.isDuplicate) {
            setError('This XM account number is already registered to another user. Please use a different account number.');
            
            // Track duplicate account error
            if (analytics) {
              logEvent(analytics, 'profile_save_error', {
                error_type: 'xm_account_duplicate',
                error_message: 'XM account already registered',
                device_type: deviceType,
                user_id: currentUser.uid,
                xm_account_number: formData.xmAccountNumber?.trim()
              });
            }
            
            setSaving(false);
            return;
          }
        }
      }
      
      const displayName = `${formData.firstName} ${formData.lastName}`.trim();
      
      // Update Firebase Auth profile
      await updateProfile(currentUser, { displayName });

      // Prepare the update object
      const updateData: any = {
        displayName,
        mobileNumber: formData.mobileNumber,
        bio: formData.bio,
        country: formData.country,
        tradingExperience: formData.tradingExperience,
        preferredTradingPairs: formData.preferredTradingPairs,
        timezone: formData.timezone,
        newsletter: formData.newsletter,

        firstName: formData.firstName,
        lastName: formData.lastName,
        // Trading Account Information
        xmAccountNumber: formData.xmAccountNumber,
        weltradeAccountNumber: formData.weltradeAccountNumber,
        weltradeUserId: formData.weltradeUserId,
        derivAccountNumber: formData.derivAccountNumber,
        updatedAt: serverTimestamp(),
      };

      // If XM account number changed, reset verification status
      if (xmAccountChanged) {
        updateData.xmAffiliationVerified = false;
        updateData.xmAffiliationVerifiedAt = null;
        
        // Update local state to reflect the change
        setFormData(prev => ({ ...prev, xmAffiliationVerified: false }));
        setAffiliationStatus('unchecked');
        setAffiliationMessage('XM account changed - verification required');
        

      }

      // Update Firestore document
      await updateDoc(doc(db, 'users', currentUser.uid), updateData);



      // Update local profile state
      const updatedProfile = profile ? {
        ...profile,
        displayName,
        ...formData,
        // If XM account changed, ensure verification status is updated in profile state
        ...(xmAccountChanged && {
          xmAffiliationVerified: false,
          xmAffiliationVerifiedAt: null
        })
      } : null;
      
      setProfile(updatedProfile);
      
      // Track successful profile save
      if (analytics) {
        logEvent(analytics, 'profile_save_success', {
          device_type: deviceType,
          user_id: currentUser.uid,
          xm_account_changed: xmAccountChanged,
          affiliation_status: affiliationStatus,
          has_trading_experience: !!(formData.tradingExperience?.trim()),
          has_preferred_pairs: !!(formData.preferredTradingPairs?.trim()),
          has_bio: !!(formData.bio?.trim()),
          has_country: !!(formData.country?.trim())
        });
      }
      
      if (xmAccountChanged) {
        setSuccess('Profile updated successfully! Please verify your new XM account.');
      } else {
        setSuccess('Profile updated successfully!');
      }

      // Recalculate XM progress after profile update
      try {
        if (updatedProfile) {
          await pointsService.calculateXmLevel(currentUser.uid, updatedProfile);
          
          // Notify other components that the level may have changed
          window.dispatchEvent(new CustomEvent('userLevelUpdated'));
        }
      } catch (progressError) {
        // Don't show error to user as the profile save was successful
      }
    } catch (error: any) {
      setError('Failed to update profile: ' + (error.message || 'Unknown error'));
      
      // Track general profile save error
      if (analytics) {
        logEvent(analytics, 'profile_save_error', {
          error_type: 'general_error',
          error_message: error.message || 'Unknown error',
          device_type: deviceType,
          user_id: currentUser.uid
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCheckAffiliation = async () => {
    if (!formData.xmAccountNumber || formData.xmAccountNumber.trim() === '') {
      setAffiliationStatus('error');
      setAffiliationMessage('Please enter an XM account number first');
      
      // Track XM verification error - missing account number
      if (analytics) {
        logEvent(analytics, 'xm_verification_error', {
          error_type: 'missing_account_number',
          error_message: 'No XM account number provided',
          device_type: deviceType,
          user_id: currentUser?.uid
        });
      }
      
      return;
    }

    setAffiliationStatus('checking');
    setAffiliationMessage('Checking affiliation...');

    // Track XM verification attempt
    if (analytics) {
      logEvent(analytics, 'xm_verification_start', {
        device_type: deviceType,
        user_id: currentUser?.uid,
        xm_account_number: formData.xmAccountNumber?.trim()
      });
    }

    try {
      // Check for duplicate XM account number before checking affiliation
      if (currentUser) {
        const currentXmAccount = profile?.xmAccountNumber || '';
        
        // Only check for duplicates if the XM account number has changed
        if (formData.xmAccountNumber.trim() !== currentXmAccount.trim()) {
          const duplicateCheck = await pointsService.checkDuplicateXmAccount(
            formData.xmAccountNumber.trim(), 
            currentUser.uid
          );
          
          if (duplicateCheck.isDuplicate) {
            setAffiliationStatus('error');
            setAffiliationMessage('❌ This XM account number is already registered to another user');
            
            // Track duplicate XM account during verification
            if (analytics) {
              logEvent(analytics, 'xm_verification_error', {
                error_type: 'duplicate_account',
                error_message: 'XM account already registered to another user',
                device_type: deviceType,
                user_id: currentUser?.uid,
                xm_account_number: formData.xmAccountNumber?.trim()
              });
            }
            
            return;
          }
        }
      }
      const response = await fetch(
        `https://us-central1-t1-fx-team.cloudfunctions.net/checkAffiliation?accountNumber=${encodeURIComponent(formData.xmAccountNumber)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        if (data.isAffiliated) {
          setAffiliationStatus('affiliated');
          setAffiliationMessage('✅ Account is affiliated with T1 FX Team');
          
          // Track successful XM verification
          if (analytics) {
            logEvent(analytics, 'xm_verification_success', {
              device_type: deviceType,
              user_id: currentUser?.uid,
              xm_account_number: formData.xmAccountNumber?.trim(),
              affiliation_confirmed: true
            });
          }
          
          // Save affiliation status to user data immediately
          if (currentUser) {
            try {
              await updateDoc(doc(db, 'users', currentUser.uid), {
                xmAffiliationVerified: true,
                xmAffiliationVerifiedAt: serverTimestamp(),
                xmAccountNumber: formData.xmAccountNumber, // Also save the verified account number
                updatedAt: serverTimestamp(),
              });
              
              // Update local state
              setFormData(prev => ({ ...prev, xmAffiliationVerified: true }));
              setProfile(prev => prev ? { 
                ...prev, 
                xmAffiliationVerified: true,
                xmAffiliationVerifiedAt: new Date(),
                xmAccountNumber: formData.xmAccountNumber
              } : null);
              

              
              // Trigger level recalculation since XM verification affects Bronze level
              try {
                const updatedProfileData = { 
                  ...profile, 
                  xmAffiliationVerified: true,
                  xmAccountNumber: formData.xmAccountNumber
                };
                await pointsService.calculateXmLevel(currentUser.uid, updatedProfileData);
                
                // Notify other components that the level may have changed
                window.dispatchEvent(new CustomEvent('userLevelUpdated'));
              } catch (calcError) {
                // Level calculation error - not critical
              }
            } catch (saveError) {
              // Don't show error to user as the verification still worked
            }
          }
        } else {
          setAffiliationStatus('not-affiliated');
          setAffiliationMessage('❌ Account is not affiliated with T1 FX Team');
          
          // Track failed XM verification
          if (analytics) {
            logEvent(analytics, 'xm_verification_failed', {
              device_type: deviceType,
              user_id: currentUser?.uid,
              xm_account_number: formData.xmAccountNumber?.trim(),
              affiliation_confirmed: false,
              reason: 'not_affiliated'
            });
          }
        }
      } else {
        setAffiliationStatus('error');
        setAffiliationMessage('Error checking affiliation: ' + (data.message || 'Unknown error'));
        
        // Track XM verification API error
        if (analytics) {
          logEvent(analytics, 'xm_verification_error', {
            error_type: 'api_error',
            error_message: data.message || 'Unknown error',
            device_type: deviceType,
            user_id: currentUser?.uid,
            xm_account_number: formData.xmAccountNumber?.trim(),
            response_status: response.status
          });
        }
      }
    } catch (error: any) {
      setAffiliationStatus('error');
      setAffiliationMessage('Network error: Unable to check affiliation');
      
      // Track XM verification network error
      if (analytics) {
        logEvent(analytics, 'xm_verification_error', {
          error_type: 'network_error',
          error_message: error.message || 'Network error',
          device_type: deviceType,
          user_id: currentUser?.uid,
          xm_account_number: formData.xmAccountNumber?.trim()
        });
      }
    }
  };

  const handleChangePassword = async () => {
    if (!currentUser?.email) return;

    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    // Track password change attempt
    if (analytics) {
      logEvent(analytics, 'password_change_attempt', {
        device_type: deviceType,
        user_id: currentUser.uid
      });
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all password fields');
      
      // Track validation error
      if (analytics) {
        logEvent(analytics, 'password_change_error', {
          error_type: 'missing_fields',
          error_message: 'Missing required fields',
          device_type: deviceType,
          user_id: currentUser.uid
        });
      }
      
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      
      // Track validation error
      if (analytics) {
        logEvent(analytics, 'password_change_error', {
          error_type: 'password_mismatch',
          error_message: 'Passwords do not match',
          device_type: deviceType,
          user_id: currentUser.uid
        });
      }
      
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      
      // Track validation error
      if (analytics) {
        logEvent(analytics, 'password_change_error', {
          error_type: 'password_too_short',
          error_message: 'Password less than 6 characters',
          device_type: deviceType,
          user_id: currentUser.uid,
          password_length: newPassword.length
        });
      }
      
      return;
    }

    setChangingPassword(true);
    setError('');

    try {
      // Re-authenticate user before changing password
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      
      // Update password
      await updatePassword(currentUser, newPassword);
      
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSuccess('Password changed successfully!');
      
      // Track successful password change
      if (analytics) {
        logEvent(analytics, 'password_change_success', {
          device_type: deviceType,
          user_id: currentUser.uid
        });
      }
    } catch (error: any) {
      if (error.code === 'auth/wrong-password') {
        setError('Current password is incorrect');
        
        // Track wrong password error
        if (analytics) {
          logEvent(analytics, 'password_change_error', {
            error_type: 'wrong_current_password',
            error_message: 'Current password is incorrect',
            device_type: deviceType,
            user_id: currentUser.uid,
            error_code: error.code
          });
        }
      } else {
        setError('Failed to change password: ' + (error.message || 'Unknown error'));
        
        // Track general password change error
        if (analytics) {
          logEvent(analytics, 'password_change_error', {
            error_type: 'general_error',
            error_message: error.message || 'Unknown error',
            device_type: deviceType,
            user_id: currentUser.uid,
            error_code: error.code || 'unknown'
          });
        }
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant={isMobile ? 'h4' : 'h2'}
            component="h1"
            gutterBottom
            sx={{ mb: 4, textAlign: 'center' }}
          >
            User Profile
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Profile Information Card */}
            <Grid size={{ xs: 12, md: 8 }}>
              <MotionCard
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" component="h2" sx={{ display: 'flex', alignItems: 'center' }}>
                      <PersonIcon sx={{ mr: 1 }} />
                      Personal Information
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="First Name"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleFormChange}
                        variant="filled"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleFormChange}
                        variant="filled"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Email"
                        value={currentUser?.email || ''}
                        disabled
                        variant="filled"
                        InputProps={{
                          startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Mobile Number"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleFormChange}
                        variant="filled"
                        InputProps={{
                          startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Bio"
                        name="bio"
                        value={formData.bio}
                        onChange={handleFormChange}
                        variant="filled"
                        multiline
                        rows={3}
                        placeholder="Tell us about yourself and your trading journey..."
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl 
                        fullWidth 
                        variant="filled"
                      >
                        <InputLabel>Country</InputLabel>
                        <Select
                          name="country"
                          value={formData.country}
                          onChange={handleFormChange}
                          label="Country"
                        >
                          <MenuItem value="">
                            <em>Select your country</em>
                          </MenuItem>
                          {countries.map((country) => (
                            <MenuItem key={country} value={country}>
                              {country}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl 
                        fullWidth 
                        variant="filled"
                      >
                        <InputLabel>Timezone</InputLabel>
                        <Select
                          name="timezone"
                          value={formData.timezone}
                          onChange={handleFormChange}
                          label="Timezone"
                        >
                          <MenuItem value="">
                            <em>Select your timezone</em>
                          </MenuItem>
                          {timezones.map((timezone) => (
                            <MenuItem key={timezone} value={timezone}>
                              {timezone}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  {/* Trading Preferences Section */}
                  {(!formData.tradingExperience?.trim() || !formData.preferredTradingPairs?.trim()) && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Complete your trading experience level and preferred trading pairs to unlock <strong>Bronze Status</strong> 🎉
                    </Alert>
                  )}

                  <Typography variant="h6" gutterBottom>
                    Trading Preferences
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl 
                        fullWidth 
                        variant="filled"
                      >
                        <InputLabel>Trading Experience</InputLabel>
                        <Select
                          name="tradingExperience"
                          value={formData.tradingExperience}
                          onChange={(e) => handleFormChange(e as React.ChangeEvent<HTMLInputElement>)}
                          label="Trading Experience"
                        >
                          <MenuItem value="">
                            <em>Select your experience level</em>
                          </MenuItem>
                          <MenuItem value="Beginner">Beginner (0-1 years)</MenuItem>
                          <MenuItem value="Intermediate">Intermediate (1-3 years)</MenuItem>
                          <MenuItem value="Advanced">Advanced (3-5 years)</MenuItem>
                          <MenuItem value="Expert">Expert (5+ years)</MenuItem>
                          <MenuItem value="Professional">Professional Trader</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Preferred Trading Pairs"
                        name="preferredTradingPairs"
                        value={formData.preferredTradingPairs}
                        onChange={handleFormChange}
                        variant="filled"
                        placeholder="e.g., EUR/USD, GBP/USD, USD/JPY"
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Typography variant="h6" gutterBottom>
                    Trading Account Information
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="XM Account Number"
                        name="xmAccountNumber"
                        value={formData.xmAccountNumber}
                        onChange={handleFormChange}
                        required
                        variant="filled"
                        placeholder="Enter your XM trading account number"
                        helperText={affiliationMessage || "Your XM trading account number (required)"}
                        InputProps={{
                          endAdornment: (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {affiliationStatus === 'affiliated' && (
                                <Tooltip title="Account is affiliated">
                                  <VerifiedIcon sx={{ color: 'success.main' }} />
                                </Tooltip>
                              )}
                              {affiliationStatus === 'not-affiliated' && (
                                <Tooltip title="Account is not affiliated">
                                  <NotVerifiedIcon sx={{ color: 'error.main' }} />
                                </Tooltip>
                              )}
                              <Button
                                size="small"
                                onClick={handleCheckAffiliation}
                                disabled={!formData.xmAccountNumber.trim() || affiliationStatus === 'checking'}
                                sx={{ minWidth: 'auto', px: 1 }}
                              >
                                {affiliationStatus === 'checking' ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  'Check'
                                )}
                              </Button>
                            </Box>
                          ),
                        }}
                        FormHelperTextProps={{
                          sx: {
                            color: affiliationStatus === 'affiliated' ? 'success.main' : 
                                   affiliationStatus === 'not-affiliated' ? 'error.main' :
                                   affiliationStatus === 'error' ? 'error.main' : 'text.secondary'
                          }
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Deriv Account Number"
                        name="derivAccountNumber"
                        value={formData.derivAccountNumber}
                        onChange={handleFormChange}
                        variant="filled"
                        placeholder="Enter your Deriv account number"
                        helperText="Your Deriv account number"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Weltrade Account Number"
                        name="weltradeAccountNumber"
                        value={formData.weltradeAccountNumber}
                        onChange={handleFormChange}
                        variant="filled"
                        placeholder="Enter your Weltrade account number"
                        helperText="Your Weltrade account number"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Weltrade User ID"
                        name="weltradeUserId"
                        value={formData.weltradeUserId}
                        onChange={handleFormChange}
                        variant="filled"
                        placeholder="Enter your Weltrade User ID"
                        helperText="Your unique Weltrade User ID"
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.newsletter}
                          onChange={handleFormChange}
                          name="newsletter"
                        />
                      }
                      label="Subscribe to newsletter and trading updates"
                    />
                  </Box>

                  <Box sx={{ mt: 3, display: 'flex', gap: 2, flexDirection: 'column' }}>
                    <Button
                      variant="contained"
                      onClick={handleSaveProfile}
                      disabled={saving || !formData.xmAccountNumber?.trim() || (!!formData.xmAccountNumber?.trim() && affiliationStatus !== 'affiliated')}
                      startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                      sx={{
                        '&.Mui-disabled': {
                          backgroundColor: theme.palette.grey[400],
                          color: theme.palette.common.white,
                          opacity: 1,
                          '&:hover': {
                            backgroundColor: theme.palette.grey[400],
                          }
                        }
                      }}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    
                    {!formData.xmAccountNumber?.trim() ? (
                      <Typography variant="caption" color="error.main" sx={{ mt: 1 }}>
                        ⚠️ XM Account Number is required to save changes
                      </Typography>
                    ) : (!!formData.xmAccountNumber?.trim() && affiliationStatus !== 'affiliated' && (
                      <Typography variant="caption" color="warning.main" sx={{ mt: 1 }}>
                        ⚠️ Please verify your XM account before saving changes
                      </Typography>
                    ))}
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>

            {/* Profile Summary & Security */}
            <Grid size={{ xs: 12, md: 4 }}>
              {/* Profile Summary */}
              <MotionCard
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                sx={{ mb: 3 }}
              >
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      fontSize: '2rem',
                      backgroundColor: theme.palette.primary.main,
                    }}
                  >
                    {currentUser?.displayName?.charAt(0)?.toUpperCase() || 
                     currentUser?.email?.charAt(0)?.toUpperCase() || 'T'}
                  </Avatar>
                  
                  <Typography variant="h6" gutterBottom>
                    {profile?.displayName || currentUser?.email}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {currentUser?.email}
                  </Typography>

                  {profile?.isAdmin && (
                    <Paper sx={{ p: 1, mt: 2, backgroundColor: theme.palette.warning.main + '20' }}>
                      <Typography variant="caption" color="warning.main" fontWeight="bold">
                        Administrator
                      </Typography>
                    </Paper>
                  )}

                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="body2" color="text.secondary">
                    Member since: {formatDate(profile?.createdAt)}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary">
                    Last login: {formatDate(profile?.lastLogin)}
                  </Typography>
                </CardContent>
              </MotionCard>

              {/* Security Settings */}
              <MotionCard
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <SecurityIcon sx={{ mr: 1 }} />
                    Security
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Change your password to keep your account secure
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      type="password"
                      label="Current Password"
                      name="currentPassword"
                      value={passwordForm.currentPassword}
                      onChange={handlePasswordChange}
                      size="small"
                    />
                    <TextField
                      fullWidth
                      type="password"
                      label="New Password"
                      name="newPassword"
                      value={passwordForm.newPassword}
                      onChange={handlePasswordChange}
                      size="small"
                    />
                    <TextField
                      fullWidth
                      type="password"
                      label="Confirm New Password"
                      name="confirmPassword"
                      value={passwordForm.confirmPassword}
                      onChange={handlePasswordChange}
                      size="small"
                    />
                    <Button
                      variant="outlined"
                      onClick={handleChangePassword}
                      disabled={changingPassword}
                      startIcon={changingPassword ? <CircularProgress size={20} /> : <SecurityIcon />}
                      fullWidth
                    >
                      {changingPassword ? 'Changing...' : 'Change Password'}
                    </Button>
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};